import React, { useEffect, useState, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObjectsWithFilter, getObject, addObject, getObjects } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import 'dayjs/locale/fr'
import Form from '../../form/Form'
import ContractPDF from './ContractPDF'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import useSaveEntity from '../../hooks/useSaveEntity'
import { buildContractPayload, convertDocumentAndUploadFile } from '../../../utils/documentManager'
import { getContractFields } from './ContractFields'
import Toolbar from '../../toolbar/Toolbar'

const ROUTES = {
  EDIT: '/contracts/edit',
  VIEW: '/contracts/view',
  ADD: '/contracts/add'
}

const EditContract = () => {
  const history = useNavigate()
  const [contract, setContract] = useState({ lines: [] })
  const [company, setCompany] = useState([])
  const [img, setImg] = useState('')
  const [isSubmit, setIsSubmit] = useState(false)
  const { user } = useAuth()
  const location = useLocation()
  const [settings, setSettings] = useState({})
  const path = location.pathname
  const contractId = location.state?.id
  const [modulesList, setModulesList] = useState([])

  const setLines = useCallback((newLines) => setContract((prev) => ({ ...prev, lines: newLines })), [])

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouveau Contrat' : `Contrat ${contract.contract_number}`
      }
    }),
    [contract, path]
  )

  const contractQuery = mode === 'edit' ? { key: 'contract', fn: getObject, args: ['contracts', contractId] } : null

  const queries = [
    { key: 'settings', fn: getObjectsWithFilter, args: ['settings', { company_id: user.company_id }] },
    { key: 'company', fn: getObject, args: ['company', user.company_id] },
    { key: 'modules', fn: getObjects, args: ['modules', {}] },
    contractQuery
  ].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'contracts',
    mode,
    id: contractId,
    user,
    settingsKey: 'contract_number',
    addFn: addObject,
    editFn: editObject,
    args: [contract],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: (res) => {
      setContract(res.data)
      convertDocumentAndUploadFile(
        <ContractPDF contract_param={res.data} company_param={company} logo_param={img} settings={settings} />,
        `contrat_${res.data.contract_number}`,
        user,
        'contract',
        'contracts',
        res.data
      )
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload: buildContractPayload
  })

  const memoizedFields = useMemo(
    () => getContractFields(user, contract, modulesList, setLines),
    [user, contract, modulesList, setLines]
  )

  const options = useMemo(
    () => ({
      fields: memoizedFields,
      data: contract
    }),
    [memoizedFields, contract]
  )

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.modules) setModulesList(fetched.modules)
      if (fetched.settings) setSettings(fetched.settings)
      if (fetched.company) {
        setCompany(fetched.company)
        const attach = fetched.company.attachments?.find((a) => a.type === 'logo')
        if (attach?.file?.data) {
          const blob = new Blob([new Uint8Array(attach.file.data)])
          const reader = new FileReader()
          reader.onloadend = () => {
            const base64 = reader.result?.toString().split(',')[1] ?? ''
            setImg(base64)
          }
          reader.readAsDataURL(blob)
        }
      }
      if (fetched.contract) setContract(fetched.contract)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (!isSubmit) return

    const cleanedContract = {
      ...contract,
      lines: (contract.lines ?? []).map((line) => ({ ...line }))
    }

    saveEntity({ data: cleanedContract })
    setIsSubmit(false)
  }, [isSubmit, contract, saveEntity])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flex: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={contract} />

      <Form
        options={options}
        mode='edit'
        setDataParent={setContract}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditContract
